import * as React from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { format, startOfMonth, isBefore, isAfter } from 'date-fns';

import { cn } from '../../lib/utils';
import { Button, buttonVariants } from './Button';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

export interface MonthPickerProps {
  /** Selected month (any date within the month) */
  value?: Date | null;
  /** Callback when month is selected */
  onChange?: (date: Date | undefined) => void;
  /** Placeholder text when no value selected */
  placeholder?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether there's an error state */
  error?: boolean;
  /** Minimum selectable month */
  minDate?: Date;
  /** Maximum selectable month */
  maxDate?: Date;
  /** Format for displaying the selected month */
  displayFormat?: string;
}

const MonthPicker = React.forwardRef<HTMLButtonElement, MonthPickerProps>(
  (
    {
      value,
      onChange,
      placeholder = 'Select month',
      disabled = false,
      className,
      error,
      minDate,
      maxDate,
      displayFormat = 'MMM yyyy'
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const [viewYear, setViewYear] = React.useState(() => {
      if (value) return value.getFullYear();
      return new Date().getFullYear();
    });

    React.useEffect(() => {
      if (value) {
        setViewYear(value.getFullYear());
      }
    }, [value]);

    const handleMonthSelect = (monthIndex: number) => {
      const selectedDate = new Date(viewYear, monthIndex, 1);
      onChange?.(selectedDate);
      setOpen(false);
    };

    const isMonthDisabled = (monthIndex: number): boolean => {
      const monthDate = new Date(viewYear, monthIndex, 1);
      if (minDate) {
        const minMonth = startOfMonth(minDate);
        if (isBefore(monthDate, minMonth)) return true;
      }
      if (maxDate) {
        const maxMonth = startOfMonth(maxDate);
        if (isAfter(monthDate, maxMonth)) return true;
      }
      return false;
    };

    const isMonthSelected = (monthIndex: number): boolean => {
      if (!value) return false;
      return value.getFullYear() === viewYear && value.getMonth() === monthIndex;
    };

    const isCurrentMonth = (monthIndex: number): boolean => {
      const today = new Date();
      return today.getFullYear() === viewYear && today.getMonth() === monthIndex;
    };

    const canNavigatePrev = (): boolean => {
      if (!minDate) return true;
      const prevYearLastMonth = new Date(viewYear - 1, 11, 1);
      return !isBefore(prevYearLastMonth, startOfMonth(minDate));
    };

    const canNavigateNext = (): boolean => {
      if (!maxDate) return true;
      const nextYearFirstMonth = new Date(viewYear + 1, 0, 1);
      return !isAfter(nextYearFirstMonth, startOfMonth(maxDate));
    };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            disabled={disabled}
            className={cn(
              'w-full justify-start text-left font-normal',
              !value && 'text-muted-foreground',
              error && 'border-destructive focus:ring-destructive',
              className
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            {value ? format(value, displayFormat) : <span>{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-3">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                className={cn(buttonVariants({ variant: 'outline' }), 'h-7 w-7 p-0')}
                disabled={!canNavigatePrev()}
                onClick={() => setViewYear((y) => y - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">{viewYear}</span>
              <Button
                variant="outline"
                className={cn(buttonVariants({ variant: 'outline' }), 'h-7 w-7 p-0')}
                disabled={!canNavigateNext()}
                onClick={() => setViewYear((y) => y + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {MONTHS.map((month, index) => {
                const isDisabled = isMonthDisabled(index);
                const isSelected = isMonthSelected(index);
                const isCurrent = isCurrentMonth(index);

                return (
                  <Button
                    key={month}
                    variant={isSelected ? 'default' : 'ghost'}
                    className={cn(
                      'h-9 w-full',
                      isSelected &&
                        'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                      !isSelected && isCurrent && 'bg-accent text-accent-foreground',
                      isDisabled && 'opacity-50 cursor-not-allowed'
                    )}
                    disabled={isDisabled}
                    onClick={() => handleMonthSelect(index)}
                  >
                    {month}
                  </Button>
                );
              })}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);
MonthPicker.displayName = 'MonthPicker';

export { MonthPicker };
